import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { beClientProfileCreate } from 'actions/client/beSidebarClientProfile.action';
import { bePatchClientPicture } from 'actions/client/beSidebarClientProfilePicture.action';
import NO_IMAGE from 'assets/images/slide-out/user-avatar.svg';
import { clientSourceOptions, dayOptionsByMonth, dropdownMonthOptions, pronounsOptions, transformClientTagsForBackend } from 'bl/client/clientProfile.bl';
import SalonFlag from 'components/core/SalonFlag.component';
import Button from 'components/form/Button.component';
import DropdownInput from 'components/form/DropdownInput.component';
import FormField from 'components/form/FormField.component';
import { MultiSelectDropdownInput } from 'components/form/MultiSelectDropdownInput.component';
import TextInput from 'components/form/TextInput.component';
import ImageCropper from 'components/generic/ImageCropper.component';
import { ClientProfilePicture } from 'components/sidebar/client-profile/common/ClientProfilePicture.component';
import { ClientProfileSaveErrorModal } from 'components/sidebar/client-profile/common/modals/ClientProfileSaveErrorModal.component';
import { ClientProfileTags } from 'components/sidebar/client-profile/common/tags/ClientProfileTags';
import PhoneInput from 'components/wrapped/PhoneInput.component';
import SF from 'constants/salonFlag.const';
import { usePrepareWriteOnly } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useDispatcher } from 'providers/Dispatcher.provider';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import fromAnyToInitials from 'utils/converters/string/fromAnyToInitials.util';
import logger from 'utils/logger.util';
import { isValidPhone } from 'utils/predicates/isValid.util';
import isEmptyString from 'utils/predicates/string/isEmptyString.util';

import BackButton from 'booking/components/common/bw-header/BackButton';
import TextField from 'booking/components/common/TextField';

import CN from './AddNewClientPanel.module.scss';

const L = logger('AddNewClientPanel');

const MAX_SELECTED_TAGS = 3;

const EMPTY_FORM = {
  user_name: '',
  phone: '',
  email: '',
  known_as: '',
  pronouns: [],
  alternative_phone: '',
  client_tags: [],
  source: '',
  dateOfBirthDay: '',
  dateOfBirthMonth: '',
  dateOfBirthYear: '',
  address: '',
};

export const AddNewClientPanel = ({ initialName, onSuccess, onBack }) => {
  const { t } = useTranslation();
  const { locale } = useGilt();
  const { isDarkMode } = useTheme();
  const salonId = useSelector(selectCurrentSalonId);

  const DAY_OPTIONS_BY_MONTH = dayOptionsByMonth(t);
  const MONTH_DROPDOWN_OPTIONS = dropdownMonthOptions(t, locale);
  const PRONOUNS_OPTIONS = pronounsOptions(t);
  const CLIENT_SOURCE_OPTIONS = clientSourceOptions(t);

  const [formData, setFormData] = useState(() => ({
    ...EMPTY_FORM,
    user_name: initialName || '',
  }));

  const [pendingImage, setPendingImage] = useState(null);
  const [pendingImagePreview, setPendingImagePreview] = useState(null);

  const { val: showImageUploadModal, yay: openImageModal, nay: closeImageModal } = useTip(false);
  const { val: isErrorModalOpen, yay: showErrorModal, nay: hideErrorModal } = useTip(false);

  const { isLoading: isCreating, up: createClient } = usePrepareWriteOnly(beClientProfileCreate);
  const patchClientPicture = useDispatcher(bePatchClientPicture);

  const initialMonthIndex = fromAnyToNumber(formData?.dateOfBirthMonth) || 1;
  const [dayOptions, setDayOptions] = useState(DAY_OPTIONS_BY_MONTH[initialMonthIndex]);

  const handleInputChange = (ev) => {
    const fieldName = ev?.target?.name || ev?.name;
    const fieldValue = ev?.target?.value || ev?.raw;

    if (fieldName) {
      setFormData((prev) => ({ ...prev, [fieldName]: fieldValue }));
    }
  };

  const handlePhoneChange = ({ name: fieldName, value }) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value.ev }));
  };

  const handleDropdownChange = ({ name: fieldName, value }) => {
    if (!fieldName) return;

    const actualItem = value?.raw || value;
    const valueToStore = actualItem?.extra || actualItem;

    setFormData((prev) => {
      const updated = { ...prev, [fieldName]: valueToStore };

      if (fieldName === 'dateOfBirthMonth') {
        const monthVal = fromAnyToNumber(valueToStore) || 1;
        const newDayOptions = DAY_OPTIONS_BY_MONTH[monthVal] || DAY_OPTIONS_BY_MONTH[1];
        setDayOptions(newDayOptions);

        const maxDays = newDayOptions.length - 1;
        const currentDay = fromAnyToNumber(prev.dateOfBirthDay);
        if (prev.dateOfBirthDay && currentDay > maxDays) {
          updated.dateOfBirthDay = '';
        }
      }

      return updated;
    });
  };

  const handleImageSave = useCallback(({ croppedImageUrl, croppedFile }) => {
    setPendingImage(croppedFile);
    setPendingImagePreview(croppedImageUrl);
  }, []);

  const handleImageRemove = useCallback(() => {
    if (pendingImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(pendingImagePreview);
    }
    setPendingImage(null);
    setPendingImagePreview(null);
  }, [pendingImagePreview]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (isEmptyString(formData?.user_name)) return;

      try {
        const payload = {
          address: fromAnyToTrimmed(formData.address) || null,
          alternative_phone: fromAnyToTrimmed(formData.alternative_phone) || null,
          birthyear: formData.dateOfBirthYear ? fromAnyToInteger(formData.dateOfBirthYear) : null,
          birthday: formData.dateOfBirthDay && formData.dateOfBirthMonth ? [fromAnyToInteger(formData.dateOfBirthDay), fromAnyToInteger(formData.dateOfBirthMonth)] : null,
          client_tags: transformClientTagsForBackend(formData.client_tags),
          email: fromAnyToTrimmed(formData.email) || null,
          known_as: fromAnyToTrimmed(formData.known_as) || null,
          phone: fromAnyToTrimmed(formData.phone) || null,
          pronouns: Array.isArray(formData.pronouns) && formData.pronouns.length > 0 ? formData.pronouns : null,
          source: fromAnyToTrimmed(formData.source) || null,
          user_name: fromAnyToTrimmed(formData.user_name) || null,
          salon_id: salonId,
        };

        const res = await createClient({ data: payload });

        if (!res?.is?.ok) {
          L.warn('handleSave()', 'Client creation returned non-ok response');
          showErrorModal();
          return;
        }

        const newClientId = res?.data?.id ?? res?.json?.id;

        if (pendingImage && newClientId) {
          try {
            await patchClientPicture({ clientId: newClientId, profile_image: pendingImage });
          } catch (imgErr) {
            L.warn('handleSave()', 'Image upload failed after client creation:', imgErr);
          }
        }

        if (pendingImagePreview?.startsWith('blob:')) {
          URL.revokeObjectURL(pendingImagePreview);
        }

        L.info('handleSave()', 'Client created successfully', { newClientId });
        hideErrorModal();
        onSuccess?.(res?.data ?? res?.json ?? res);
      } catch (error) {
        L.warn('handleSave()', 'Failed to create client:', { error });
        showErrorModal();
      }
    },
    [formData, createClient, pendingImage, pendingImagePreview, patchClientPicture, onSuccess, showErrorModal, hideErrorModal, salonId],
  );

  const dayDropdownOptions = useMemo(() => dayOptions.map((opt) => ({ description: opt.label, extra: opt.value })), [dayOptions]);

  const hasInvalidPhone = formData.phone && !isValidPhone({ value: formData.phone });
  const hasInvalidAltPhone = formData.alternative_phone && !isValidPhone({ value: formData.alternative_phone });

  const isSaveDisabled = isCreating || isEmptyString(formData?.user_name) || hasInvalidPhone || hasInvalidAltPhone;

  const trimmedUserName = fromAnyToTrimmed(formData?.user_name);
  const useNamePlaceholderAvatar = !pendingImagePreview && isEmptyString(trimmedUserName);
  const profilePictureImageSrc = pendingImagePreview || (useNamePlaceholderAvatar ? NO_IMAGE : undefined);
  const profilePictureInitials = useNamePlaceholderAvatar ? '' : fromAnyToInitials(formData?.user_name);

  return (
    <form data-bem="AddNewClientPanel" className={cn({ [CN.component]: true, [CN.dark]: isDarkMode })} onSubmit={handleSubmit} noValidate>
      <div className={CN.form}>
        {onBack && <BackButton onClick={onBack} />}

        {/* Profile Picture Section */}
        <div data-bem="AddNewClientPanel__profile-picture-section" className={CN.formContainer}>
          <span className={CN.sectionTitle}>{t('scheduler.newClient.addNewClient')}</span>
          <ClientProfilePicture imageSrc={profilePictureImageSrc} initials={profilePictureInitials} onEditClick={openImageModal} />
        </div>

        {/* Form Fields */}
        <div data-bem="AddNewClientPanel__form-container" className={CN.formContainer}>
          {/* Name */}
          <div data-bem="AddNewClientPanel__username-row" className={CN.formField}>
            <FormField
              input={TextInput}
              label={t('scheduler.newClient.clientNameLabel')}
              name="user_name"
              value={formData.user_name}
              onChange={handleInputChange}
              placeholder={t('scheduler.newClient.clientNamePlaceholder')}
            />
          </div>

          {/* Phone */}
          <div data-bem="AddNewClientPanel__phone-row" className={CN.formField}>
            <span>{t('scheduler.newClient.mobileNumberLabel')}</span>
            <div className={CN.phoneInputWrapper}>
              <PhoneInput
                data-tag="phoneInput"
                name="phone"
                value={formData.phone}
                onChange={(value) => handlePhoneChange({ name: 'phone', value })}
                placeholder={t('scheduler.newClient.mobileNumberPlaceholder')}
              />
              {hasInvalidPhone && <span className={CN.errorMessage}>{t('common.errors.validation.notValidPhoneNumber')}</span>}
            </div>
          </div>

          {/* Email */}
          <div data-bem="AddNewClientPanel__email-row" className={CN.formField}>
            <FormField
              input={TextInput}
              label={t('scheduler.newClient.emailLabel')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('scheduler.newClient.emailPlaceholder')}
            />
          </div>

          {/* Nickname */}
          <div data-bem="AddNewClientPanel__nickname-row" className={CN.formField}>
            <FormField
              input={TextInput}
              label={t('scheduler.newClient.nicknameLabel')}
              name="known_as"
              value={formData.known_as}
              onChange={handleInputChange}
              placeholder={t('scheduler.newClient.nicknamePlaceholder')}
            />
          </div>

          {/* Pronouns */}
          <div data-bem="AddNewClientPanel__pronouns-row" className={CN.formField}>
            <span>{t('scheduler.newClient.pronounsLabel')}</span>
            <MultiSelectDropdownInput
              name="pronouns"
              value={formData.pronouns}
              onChange={(value) => handleDropdownChange({ name: 'pronouns', value })}
              options={PRONOUNS_OPTIONS}
              maxSelections={4}
              separator="/"
              placeholder={t('scheduler.newClient.pronounsPlaceholder')}
              searchPlaceholder={t('scheduler.newClient.pronounsSearch')}
              isButtonLargeHeight
            />
          </div>

          {/* Alternative Phone */}
          <div data-bem="AddNewClientPanel__alt-phone-row" className={CN.formField}>
            <span>{t('scheduler.newClient.alternativeNumberLabel')}</span>
            <div className={CN.phoneInputWrapper}>
              <PhoneInput
                data-tag="phoneInput"
                name="alternative_phone"
                value={formData.alternative_phone}
                onChange={(value) => handlePhoneChange({ name: 'alternative_phone', value })}
                placeholder={t('scheduler.newClient.alternativeNumberPlaceholder')}
              />
              {hasInvalidAltPhone && <span className={CN.errorMessage}>{t('common.errors.validation.notValidPhoneNumber')}</span>}
            </div>
          </div>

          {/* Client Tags */}
          <SalonFlag name={SF.clientTags}>
            <SalonFlag.IfEnabled>
              <div data-bem="AddNewClientPanel__tags-section" className={CN.tagsSection}>
                <span className={CN.tagsLabel}>
                  {t('scheduler.newClient.clientTags', {
                    currentTags: (formData.client_tags || []).length,
                    maxTags: MAX_SELECTED_TAGS,
                  })}
                </span>
                <ClientProfileTags salonId={salonId} selectedTags={formData.client_tags || []} onTagChange={(selectedTags) => setFormData((prev) => ({ ...prev, client_tags: selectedTags.value }))} />
              </div>
            </SalonFlag.IfEnabled>
          </SalonFlag>

          {/* Source */}
          <div data-bem="AddNewClientPanel__source-row" className={CN.formField}>
            <span>{t('scheduler.newClient.sourceLabel')}</span>
            <DropdownInput
              name="source"
              value={CLIENT_SOURCE_OPTIONS.find((s) => s.extra === formData.source)}
              onChange={(value) => handleDropdownChange({ name: 'source', value })}
              options={CLIENT_SOURCE_OPTIONS}
              customArrowIconVariant={'arrowDown'}
              placeholder={t('scheduler.newClient.sourcePlaceholder')}
              isFullWidthList
              isButtonLargeHeight
            />
          </div>

          {/* Date of Birth */}
          <div data-bem="AddNewClientPanel__date-of-birth-row" className={CN.formField}>
            <span>{t('scheduler.newClient.dateOfBirthLabel')}</span>
            <div className={CN.datePickerRow}>
              <DropdownInput
                name="dateOfBirthDay"
                value={dayDropdownOptions.find((o) => fromAnyToString(o.extra) === fromAnyToString(formData.dateOfBirthDay))}
                onChange={(value) => handleDropdownChange({ name: 'dateOfBirthDay', value })}
                align="top"
                options={dayDropdownOptions}
                customArrowIconVariant={'arrowDown'}
                isFullWidthList
                isButtonLargeHeight
              />

              <DropdownInput
                name="dateOfBirthMonth"
                value={MONTH_DROPDOWN_OPTIONS.find((o) => fromAnyToString(o.extra) === fromAnyToString(formData.dateOfBirthMonth))}
                align="top"
                onChange={(value) => handleDropdownChange({ name: 'dateOfBirthMonth', value })}
                options={MONTH_DROPDOWN_OPTIONS}
                customArrowIconVariant={'arrowDown'}
                isFullWidthList
                isButtonLargeHeight
              />

              <div className={CN.yearInput}>
                <FormField
                  input={TextInput}
                  name="dateOfBirthYear"
                  value={formData.dateOfBirthYear}
                  onChange={handleInputChange}
                  placeholder={t('scheduler.newClient.yearPlaceholder')}
                  label=""
                  type="number"
                >
                  <FormField.Before />
                </FormField>
              </div>
            </div>
          </div>

          {/* Address */}
          <div data-bem="AddNewClientPanel__address-row" className={CN.formField}>
            <TextField labelName={t('scheduler.newClient.clientAddressLabel')} htmlFor="address">
              <textarea id="address" name="address" value={formData.address} onChange={handleInputChange} placeholder={t('scheduler.newClient.addressPlaceholder')} />
            </TextField>
          </div>
        </div>

        <ImageCropper
          isOpen={showImageUploadModal}
          imageSrc={pendingImagePreview}
          onClose={closeImageModal}
          onSave={handleImageSave}
          onRemove={handleImageRemove}
          modalTitle={t('scheduler.newClient.uploadImage')}
          hasControls
        />
      </div>

      {/* Footer with save button */}
      <div data-bem="AddNewClientPanel__footer" className={CN.footer}>
        <Button variant="large" width="fluid" type="submit" disabled={isSaveDisabled}>
          {isCreating ? t('scheduler.newClient.saving') : t('scheduler.newClient.saveNewClient')}
        </Button>
      </div>

      <ClientProfileSaveErrorModal isOpen={isErrorModalOpen} onClose={hideErrorModal} />
    </form>
  );
};
